/**
 * Centralized utility for service type map pin icons
 * These are the consistent icons used across:
 * - Marketplace map pins
 * - Wizard type selectors
 * - Detail modals
 * - Listing cards
 * 
 * Pattern follows jobTypeIcons.ts structure
 */

// Circle-pin SVGs (for UI elements: cards, wizard, modals)
import serviceOfferMapPin from '@/assets/service-offer-map-pin.svg';
import capabilityListingMapPin from '@/assets/capability-listing-map-pin.svg';
import serviceRequestMapPin from '@/assets/service-request-map-pin.svg';
import rfqRequestMapPin from '@/assets/rfq-request-map-pin.svg';

// Icon-only SVGs (for Google Map markers - no circle background)
import serviceOfferMarkerIcon from '@/assets/service-offer-marker-icon.svg';
import capabilityListingMarkerIcon from '@/assets/capability-listing-marker-icon.svg';
import serviceRequestMarkerIcon from '@/assets/service-request-marker-icon.svg';
import rfqRequestMarkerIcon from '@/assets/rfq-request-marker-icon.svg';

export type ServiceWizardType = 'service_offer' | 'capability_listing' | 'service_request' | 'rfq_request';
export type ServiceCategory = 'mro' | 'camo' | 'part21' | 'training' | 'operator' | 'engineering' | 'ground_ops' | 'fbo_heliport' | 'management' | 'uas';

// Map pin SVG icons for each service type (with white circle - for UI elements)
export const SERVICE_TYPE_MAP_PINS: Record<ServiceWizardType, string> = {
  service_offer: serviceOfferMapPin,
  capability_listing: capabilityListingMapPin,
  service_request: serviceRequestMapPin,
  rfq_request: rfqRequestMapPin,
};

// Icon-only SVGs for each service type (no circle - for Google Maps markers)
export const SERVICE_TYPE_MARKER_ICONS: Record<ServiceWizardType, string> = {
  service_offer: serviceOfferMarkerIcon,
  capability_listing: capabilityListingMarkerIcon,
  service_request: serviceRequestMarkerIcon,
  rfq_request: rfqRequestMarkerIcon,
};

/**
 * Get the map pin icon for a specific service listing type (with circle background)
 * Use this for UI elements: cards, wizard selector, modal headers
 */
export const getServiceTypeMapPin = (listingType?: string): string => {
  if (!listingType) return serviceOfferMapPin;
  
  switch (listingType) {
    case 'service_offer': return serviceOfferMapPin;
    case 'capability_listing': return capabilityListingMapPin;
    case 'service_request': return serviceRequestMapPin;
    case 'rfq_request': return rfqRequestMapPin;
    default: return serviceOfferMapPin;
  }
};

/**
 * Get the marker icon for a specific service listing type (icon only, no circle)
 * Use this for Google Maps markers where createIconWithBackground adds the circle
 */
export const getServiceTypeMarkerIcon = (listingType?: string): string => {
  if (!listingType) return serviceOfferMarkerIcon;
  
  switch (listingType) {
    case 'service_offer': return serviceOfferMarkerIcon;
    case 'capability_listing': return capabilityListingMarkerIcon;
    case 'service_request': return serviceRequestMarkerIcon;
    case 'rfq_request': return rfqRequestMarkerIcon;
    default: return serviceOfferMarkerIcon;
  }
};

/**
 * Configuration for service type display including labels and colors
 */
export const SERVICE_TYPE_CONFIG: Record<ServiceWizardType, {
  label: string;
  shortLabel: string;
  isOffering: boolean;
  color: string;
  bgColor: string;
}> = {
  service_offer: {
    label: 'Service Provider',
    shortLabel: 'Provider',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  capability_listing: {
    label: 'Capability Listing',
    shortLabel: 'Capability',
    isOffering: true,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  service_request: {
    label: 'Service Request',
    shortLabel: 'Request',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  rfq_request: {
    label: 'Request for Quote',
    shortLabel: 'RFQ',
    isOffering: false,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
};

/**
 * Category-specific configuration with icons and labels
 */
export const SERVICE_CATEGORY_CONFIG: Record<ServiceCategory, {
  label: string;
  shortLabel: string;
  iconName: string;
  color: string;
  bgColor: string;
}> = {
  mro: {
    label: 'MRO / Part-145',
    shortLabel: 'MRO',
    iconName: 'Wrench',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  camo: {
    label: 'CAMO',
    shortLabel: 'CAMO',
    iconName: 'FileCheck',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  part21: {
    label: 'Part-21 DOA/POA',
    shortLabel: 'Part-21',
    iconName: 'Award',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  training: {
    label: 'Training & Education',
    shortLabel: 'Training',
    iconName: 'GraduationCap',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  operator: {
    label: 'Operator Services',
    shortLabel: 'Operator',
    iconName: 'Helicopter',
    color: 'text-sky-600',
    bgColor: 'bg-sky-100',
  },
  engineering: {
    label: 'Engineering & Technical',
    shortLabel: 'Engineering',
    iconName: 'Cog',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
  },
  ground_ops: {
    label: 'Ground Operations',
    shortLabel: 'Ground Ops',
    iconName: 'Truck',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  fbo_heliport: {
    label: 'FBO / Heliport',
    shortLabel: 'FBO',
    iconName: 'Building2',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  management: {
    label: 'Management Services',
    shortLabel: 'Management',
    iconName: 'Briefcase',
    color: 'text-rose-600',
    bgColor: 'bg-rose-100',
  },
  uas: {
    label: 'Drone / UAS Services',
    shortLabel: 'UAS',
    iconName: 'Radar',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
  },
};

/**
 * Check if a service listing type is an offering (provider)
 */
export const isServiceOffering = (listingType?: string): boolean => {
  return listingType === 'service_offer' || listingType === 'capability_listing';
};

/**
 * Check if a service listing type is a request
 */
export const isServiceRequest = (listingType?: string): boolean => {
  return listingType === 'service_request' || listingType === 'rfq_request';
};

/**
 * Get category label from category value
 */
export const getServiceCategoryLabel = (category?: string): string => {
  if (!category) return 'Service';
  return SERVICE_CATEGORY_CONFIG[category as ServiceCategory]?.label || category;
};

/**
 * Get category short label from category value
 */
export const getServiceCategoryShortLabel = (category?: string): string => {
  if (!category) return 'Service';
  return SERVICE_CATEGORY_CONFIG[category as ServiceCategory]?.shortLabel || category;
};
